import { useState } from 'react'
import { ChevronDownIcon } from '@heroicons/react/24/outline'
import { CheckCircleIcon } from '@heroicons/react/24/solid'
import { Button } from '@cottage/design-system/internal-tooling/ui'
import type { MonthlyPerformanceCheck } from '@/types/monthlyPerformance'
import { formatCheckTitle, sortReportChecks } from './monthlyPerformanceUtils'

const COMMENTARY_UNVERIFIED_INSTRUCTION =
  'Dollar and percent figures in the highlighted commentary fields could not be matched to the report tables. Edit those fields and save, or acknowledge if the wording is intentional.'

type ChecksCardProps = {
  checks: MonthlyPerformanceCheck[]
  acknowledgedChecks?: string[]
  unverifiedSentences?: string[]
  onAcknowledge?: (key: string) => void
  acknowledgeDisabled?: boolean
}

function CheckStatusGlyph({ check }: { check: MonthlyPerformanceCheck }) {
  if (check.ok) {
    return (
      <CheckCircleIcon
        className="mt-0.5 h-5 w-5 shrink-0 text-emerald-600"
        aria-hidden="true"
      />
    )
  }
  const dotClass = check.severity === 'blocking' ? 'bg-red-600' : 'bg-amber-500'
  return (
    <span className="mt-2 flex w-5 shrink-0 justify-center" aria-hidden>
      <span className={`h-2 w-2 rounded-full ${dotClass}`} />
    </span>
  )
}

type CheckRowProps = {
  check: MonthlyPerformanceCheck
  acknowledgedChecks: string[]
  unverifiedSentences: string[]
  onAcknowledge?: (key: string) => void
  acknowledgeDisabled?: boolean
}

function CheckRow({
  check,
  acknowledgedChecks,
  unverifiedSentences,
  onAcknowledge,
  acknowledgeDisabled,
}: CheckRowProps) {
  const title = formatCheckTitle(check.key, check.ok)
  const isAcknowledged = !check.ok && acknowledgedChecks.includes(check.key)
  const showAcknowledge =
    !check.ok &&
    check.severity === 'advisory' &&
    !isAcknowledged &&
    onAcknowledge &&
    !acknowledgeDisabled

  const unverifiedDetail =
    !check.ok &&
    check.key === 'commentary_unverified' &&
    unverifiedSentences.length > 0
      ? unverifiedSentences
      : null
  const fallbackDetail = !check.ok && check.detail.trim().length > 0 ? check.detail : ''

  return (
    <li className="border-b border-gray-100 last:border-b-0">
      <div className="flex items-start gap-2 px-4 py-3">
        <CheckStatusGlyph check={check} />
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-start justify-between gap-2">
            <p className="text-sm font-medium text-gray-900">{title}</p>
            {showAcknowledge ? (
              <Button
                variant="outline"
                size="sm"
                type="button"
                onClick={() => onAcknowledge(check.key)}
              >
                Acknowledge
              </Button>
            ) : null}
            {isAcknowledged ? (
              <span className="text-xs font-medium text-gray-500">Acknowledged</span>
            ) : null}
          </div>
          {!check.ok && check.key === 'commentary_unverified' ? (
            <p className="mt-1 text-sm text-amber-800">{COMMENTARY_UNVERIFIED_INSTRUCTION}</p>
          ) : null}
          {unverifiedDetail ? (
            <ul className="mt-1 list-disc space-y-1 pl-5 text-sm text-amber-800">
              {unverifiedDetail.map((sentence) => (
                <li key={sentence}>{sentence}</li>
              ))}
            </ul>
          ) : null}
          {!unverifiedDetail && fallbackDetail ? (
            <p className="mt-1 text-sm text-amber-800">{fallbackDetail}</p>
          ) : null}
        </div>
      </div>
    </li>
  )
}

export default function ChecksCard({
  checks,
  acknowledgedChecks = [],
  unverifiedSentences = [],
  onAcknowledge,
  acknowledgeDisabled = false,
}: ChecksCardProps) {
  const [expanded, setExpanded] = useState(true)

  if (!checks.length) return null

  const sorted = sortReportChecks(checks)
  const passedCount = checks.filter((check) => check.ok).length

  return (
    <section
      className={`flex min-h-0 flex-col overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm ${
        expanded ? 'shrink-0' : 'shrink-0'
      }`}
      aria-label="Report checks"
    >
      <div className="flex shrink-0 items-center justify-between gap-2 border-b border-gray-200 px-4 py-3">
        <h2 className="text-base font-semibold text-gray-900">
          Checks
          {!expanded ? (
            <span className="ml-2 text-sm font-normal text-gray-600">
              {passedCount}/{checks.length} passed
            </span>
          ) : null}
        </h2>
        <Button
          variant="ghost"
          size="sm"
          type="button"
          className="gap-1"
          aria-expanded={expanded}
          onClick={() => setExpanded((open) => !open)}
        >
          {expanded ? 'Collapse' : 'Expand'}
          <ChevronDownIcon
            className={`h-4 w-4 shrink-0 transition-transform ${expanded ? '' : '-rotate-90'}`}
            aria-hidden
          />
        </Button>
      </div>
      {expanded ? (
        <ul className="min-h-0 overflow-y-auto">
          {sorted.map((check) => (
            <CheckRow
              key={check.key}
              check={check}
              acknowledgedChecks={acknowledgedChecks}
              unverifiedSentences={unverifiedSentences}
              onAcknowledge={onAcknowledge}
              acknowledgeDisabled={acknowledgeDisabled}
            />
          ))}
        </ul>
      ) : null}
    </section>
  )
}
